import React, { useState, useEffect, useMemo } from "react";
import { format } from "d3-format";
import { scaleLinear } from "d3-scale";
import sortBy from "lodash.sortby";

import Localization from "../../Localization";

import "./styles.scss";

const formatNumber = format(",.0f");
const xScale = scaleLinear();
const minWidth = 180; // (1.5em ~= 24px * 6.25)

const sections = [0, 1, 2, 3, 4, 5, 6, 7, 8];

export default function Summary(props) {
  const [tooltipData, setTooltipData] = useState(null);

  const [activeProps, setActiveProps] = useState(props);
  useEffect(() => {
    const delay = props.delay ? 600 : 0;
    const delayHandle = setTimeout(() => setActiveProps(props), delay);
    return () => clearTimeout(delayHandle);
  }, [props]);

  const {
    data,
    width,
    valueKey,
    isCapacity,
    geoSelection,
    language,
    focusedYear,
  } = activeProps;

  function hoverSection(event, datum) {
    if ("clientX" in event && datum) {
      const x = event.clientX;
      setTooltipData({ datum, x });
    } else {
      setTooltipData(null);
    }
  }

  const capacitySummary = useMemo(() => {
    const geoLabel = geoSelection === "" ? "Global" : geoSelection;
    const localGeoLabel =
      language === "zh" ? Localization.get(geoLabel.toUpperCase()) : geoLabel;
    const capacity =
      isCapacity && data.length
        ? data.find((d) => d.name === "capacity")
        : { capacity: 0, co2emissions: 0 };

    return (
      <div className="capacity">
        <div className="section">
          <div className="labels label">
            <div className="line">{localGeoLabel}</div>
            <div className="line">{Localization.get("Capacity")}</div>
          </div>
          <div className="number">{formatNumber(capacity.capacity)}</div>
          <div className="label">MW</div>
        </div>
        <div className="section">
          <div className="labels label">
            <div className="line">
              {localGeoLabel} {Localization.get("Annual")}
            </div>
            <div className="line">
              CO<sub>2</sub>
              <span>{` ${Localization.get("Emissions")}`}</span>
            </div>
          </div>
          <div className="number">{formatNumber(capacity.co2emissions)}</div>
          <div className="label">{Localization.get("K Ton")}</div>
        </div>
      </div>
    );
  }, [data, geoSelection, isCapacity, language]);

  const sectionSummary = useMemo(() => {
    const sortedData = sortBy(data, (d) => -d[valueKey]);
    const max = Math.max(...data.map((d) => d.value));
    xScale.domain([0, max]);

    return (
      <>
        {sections.map((s) => {
          const inRange = s < sortedData.length;
          const datum = inRange
            ? sortedData[s]
            : { name: "", value: 0, fill: "#fff" };
          const sectionWidth = inRange ? xScale(datum.value) * width : 0;
          const overWidth = sectionWidth >= minWidth;
          return (
            <div
              key={`section-${s}`}
              className={`section animated ${inRange ? "" : "collapsed"}`}
              style={{
                width: sectionWidth,
                background: datum.fill,
                cursor: overWidth ? "auto" : "pointer",
              }}
              onMouseMove={(e) => (overWidth ? null : hoverSection(e, datum))}
              onMouseOut={(e) => (overWidth ? null : hoverSection(e, null))}
            >
              <div className="label">
                {datum.name === "capacity"
                  ? datum.name
                  : Localization.get(datum.name, false, language)}
              </div>
              {overWidth ? (
                <div className="value">
                  <div className="number">{formatNumber(datum.value)}</div>
                  <div className="label">
                    {valueKey === "capacity" ? "MW" : Localization.get("K Ton")}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </>
    );
  }, [data, valueKey, width, language]);

  return (
    <div className="Summary">
      {data.length ? (isCapacity ? capacitySummary : sectionSummary) : null}
      {tooltipData === null ? null : (
        <div
          key={tooltipData.datum.name}
          className="tooltip section"
          style={{
            background: tooltipData.datum.fill,
            left: tooltipData.x,
          }}
        >
          <div className="label">
            {Localization.get(tooltipData.datum.name)}
          </div>
          <div className="value">
            <div className="number">
              {formatNumber(tooltipData.datum.value)}
            </div>
            <div className="label">
              {valueKey === "capacity" ? "MW" : Localization.get("K Ton")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
